/* IMPACT proxy endpoints (service/main.py's `/impact/*` routes — the
 * session cookie never reaches the browser). Two old call sites build the
 * same /impact/timeline query with slightly different params:
 *   - loadTimeline() (~1749-1766, ★ IMPACT view / §1): brand stripped of
 *     any " (..." suffix, max_polls '10'.
 *   - mvLoad() (~2129-2147, "Has this moved?" mini-analysis): brand as-is,
 *     max_polls '12'.
 * Both are F2b territory (the §1 search view); this module only owns the
 * fetch + the in-flight-dedupe/memo layer so neither view re-fetches the
 * same brand × term × location × window × max_polls twice while porting
 * proceeds.
 */
import { request } from './client.ts';
import type { SerpElement, SerpResult } from '../types/contract.ts';

/** One polled SERP snapshot in a /impact/timeline response. */
export interface TimelinePoll {
  capturedAt: string;
  location?: string | null;
  query?: string | null;
  elements?: SerpElement[];
  features?: Array<{ type?: string; [k: string]: unknown }>;
  results: SerpResult[];
  [k: string]: unknown;
}

export interface TimelineResponse {
  available: boolean;
  reason?: string | null;
  term?: string;
  location?: string;
  polls?: TimelinePoll[];
  totalPollDays?: number;
  [k: string]: unknown;
}

/** One term × location tracking row in a /impact/url response. */
export interface UrlCombo {
  term: string;
  location?: string | null;
  best?: number;
  mean?: number;
  stdev?: number;
  presencePct?: number;
  totalPollDays?: number;
  firstSeen?: string;
  series?: Array<{ d?: string; pos: number; [k: string]: unknown }>;
  ratingDrift?: string[] | null;
  ownershipDrift?: string[] | null;
  [k: string]: unknown;
}

/** GET /impact/url — the result-popover "search story". */
export interface UrlDetailResponse {
  available?: boolean;
  reason?: string | null;
  note?: string | null;
  combos?: UrlCombo[];
  [k: string]: unknown;
}

export interface TimelineParams {
  brand: string;
  term: string;
  location?: string;
  start?: string;
  end?: string;
  max_polls?: string | number;
}

// Keyed on every request param (brand|term|location|start|end|max_polls).
// max_polls in particular must be in the key: the ★ IMPACT view (§1) fetches
// with max_polls '10' while the "Has this moved?" mini-analysis fetches the
// same term/location/window with max_polls '12' — keying on fewer fields
// than the query actually sends let one view's response silently serve the
// other's slot, returning the wrong poll depth. One memo shared by both call
// sites so two callers with identical full params — including max_polls —
// dedupe/instant-return, while any differing param (max_polls included)
// always triggers its own fetch.
const timelineMemo = new Map<string, Promise<TimelineResponse>>();

/** GET /impact/timeline. In-flight calls for the same key (all params,
 * including max_polls) share one promise (dedupe); a resolved call stays
 * memoized until the process reloads. A rejected call clears its own key
 * so a later retry can fetch again instead of replaying the same failure
 * forever. */
export function fetchTimeline(params: TimelineParams): Promise<TimelineResponse> {
  const key = [
    params.brand,
    params.term,
    params.location || '',
    params.start || '',
    params.end || '',
    String(params.max_polls ?? ''),
  ].join('|');
  const cached = timelineMemo.get(key);
  if (cached) return cached;
  const q = new URLSearchParams({
    brand: params.brand,
    term: params.term,
    location: params.location || '',
    start: params.start || '',
    end: params.end || '',
    max_polls: String(params.max_polls ?? ''),
  });
  const pending = request<TimelineResponse>(`/impact/timeline?${q}`).catch((err) => {
    timelineMemo.delete(key);
    throw err;
  });
  timelineMemo.set(key, pending);
  return pending;
}

/** GET /impact/url — the result-popover "search story" (~1900-1901). No
 * memoization: unlike the timeline (browsed repeatedly while a filter
 * panel is open), this is fetched once per popover open. */
export function fetchUrlDetail(brand: string, url: string): Promise<UrlDetailResponse> {
  const q = new URLSearchParams({ brand, url });
  return request<UrlDetailResponse>(`/impact/url?${q}`);
}
